import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, CurrencyCircleDollar, Check } from "@phosphor-icons/react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENCIES = [
  { code: "BRL", symbol: "R$", name: "Real Brasileiro", flag: "\u{1F1E7}\u{1F1F7}" },
  { code: "USD", symbol: "$", name: "US Dollar", flag: "\u{1F1FA}\u{1F1F8}" },
  { code: "EUR", symbol: "\u20AC", name: "Euro", flag: "\u{1F1EA}\u{1F1FA}" },
  { code: "GBP", symbol: "\u00A3", name: "British Pound", flag: "\u{1F1EC}\u{1F1E7}" },
  { code: "ARS", symbol: "$", name: "Peso Argentino", flag: "\u{1F1E6}\u{1F1F7}" },
  { code: "MXN", symbol: "$", name: "Peso Mexicano", flag: "\u{1F1F2}\u{1F1FD}" },
  { code: "CLP", symbol: "$", name: "Peso Chileno", flag: "\u{1F1E8}\u{1F1F1}" },
  { code: "COP", symbol: "$", name: "Peso Colombiano", flag: "\u{1F1E8}\u{1F1F4}" },
  { code: "PEN", symbol: "S/", name: "Sol Peruano", flag: "\u{1F1F5}\u{1F1EA}" },
  { code: "UYU", symbol: "$", name: "Peso Uruguayo", flag: "\u{1F1FA}\u{1F1FE}" },
];

export default function ProfileCompletionModal({ user, onComplete }) {
  const [country, setCountry] = useState(user?.country || "");
  const [state, setState] = useState(user?.state || "");
  const [city, setCity] = useState(user?.city || "");
  const [currency, setCurrency] = useState(user?.currency || "BRL");
  const [saving, setSaving] = useState(false);
  const { checkAuth } = useAuth();

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/user/profile`, { country, state, city, currency });
      await checkAuth();
      toast.success("Perfil atualizado!");
      onComplete();
    } catch {
      toast.error("Erro ao salvar perfil");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onComplete(); }}>
      <DialogContent className="sm:max-w-[480px]" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <MapPin size={22} weight="bold" className="text-zinc-700" />
            Complete seu Perfil
          </DialogTitle>
          <DialogDescription>
            Adicionamos novos campos ao sistema. Informe sua localiza&ccedil;&atilde;o e moeda para uma experi&ecirc;ncia personalizada.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Pa&iacute;s</Label>
            <Input value={country} onChange={(e) => setCountry(e.target.value)}
              placeholder="ex. Brasil" className="mt-1" data-testid="profile-country" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Estado</Label>
              <Input value={state} onChange={(e) => setState(e.target.value)}
                placeholder="ex. S&atilde;o Paulo" className="mt-1" data-testid="profile-state" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Cidade</Label>
              <Input value={city} onChange={(e) => setCity(e.target.value)}
                placeholder="ex. S&atilde;o Paulo" className="mt-1" data-testid="profile-city" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700 flex items-center gap-1.5">
              <CurrencyCircleDollar size={14} /> Moeda
            </Label>
            <div className="grid grid-cols-2 gap-2 mt-1.5 max-h-40 overflow-y-auto">
              {CURRENCIES.map((cur) => (
                <button key={cur.code} type="button" onClick={() => setCurrency(cur.code)} data-testid={`profile-currency-${cur.code}`}
                  className={`flex items-center gap-2 p-2 rounded-lg border-2 text-left transition-all ${
                    currency === cur.code ? 'border-zinc-950 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-300'
                  }`}>
                  <span className="text-base">{cur.flag}</span>
                  <div>
                    <p className="text-xs font-bold text-zinc-900">{cur.code}</p>
                    <p className="text-[10px] text-zinc-500">{cur.symbol}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving}
            className="w-full bg-zinc-950 text-white hover:bg-zinc-800" data-testid="profile-save-btn">
            {saving ? "Salvando..." : "Salvar e Continuar"}
            {!saving && <Check size={16} className="ml-2" weight="bold" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
